// Demo mode — window.fetch interception.
//
// Most of what a sales demo shows does not go through the `useApi` axios hook.
// The dashboard screens, work summaries, org intelligence and every AI stream
// call `fetch` directly (there is no EventSource anywhere in this app — SSE is
// done as fetch + getReader, because EventSource cannot send an Authorization
// header). Patching `fetch` once therefore covers those paths in a single
// place, rather than editing ~50 call sites.
//
// Installed from index.js before the app renders. When demo mode is off every
// request returns the untouched original response — the patch measures one
// boolean and gets out of the way.

import {
  isDemoMode,
  maskObject,
  setKnownIdentities,
  createStreamMasker,
} from './demoMask';
import { SERVER_URL } from './serverConfig';

let installed = false;

// Responses that must never be rewritten: binaries, downloads and anything
// whose bytes the browser hands to something other than a renderer.
function isMaskableContentType(type) {
  if (!type) return false;
  return type.includes('application/json') || type.includes('text/event-stream');
}

// Server-rendered exports arrive finished; rewriting their bytes would corrupt
// the file. Callers hide these actions in demo mode instead.
function isExcludedUrl(url) {
  const u = String(url);
  return (
    u.includes('/export') ||
    u.includes('-pdf') ||
    u.includes('/voice/') ||
    u.includes('/download')
  );
}

// Harvest the real identities a payload contains so prose masking has
// something to match on. The roster usually arrives before the narrative that
// talks about it, which is what makes this work in practice.
function collectIdentities(payload, found = new Set(), depth = 0) {
  if (depth > 10 || !payload || typeof payload !== 'object') return found;

  if (Array.isArray(payload)) {
    payload.forEach((item) => collectIdentities(item, found, depth + 1));
    return found;
  }

  const first = payload.firstName || payload.first_name;
  const last = payload.lastName || payload.last_name;
  if (typeof first === 'string' && first.trim()) {
    found.add(`${first} ${typeof last === 'string' ? last : ''}`.trim());
  }
  ['name', 'fullName', 'userName', 'memberName', 'displayName'].forEach((k) => {
    const v = payload[k];
    if (typeof v === 'string' && v.trim().length > 1 && v.trim().length < 60) found.add(v.trim());
  });
  ['email', 'userEmail', 'emailAddress'].forEach((k) => {
    const v = payload[k];
    if (typeof v === 'string' && v.includes('@')) found.add(v.trim());
  });

  Object.keys(payload).forEach((k) => {
    const v = payload[k];
    if (v && typeof v === 'object') collectIdentities(v, found, depth + 1);
  });
  return found;
}

let knownIdentities = new Set();

function learnIdentities(payload) {
  const before = knownIdentities.size;
  collectIdentities(payload, knownIdentities);
  if (knownIdentities.size !== before) {
    setKnownIdentities(Array.from(knownIdentities));
  }
}

// SSE frames are `data: {...}` lines. The narrative lives in specific fields
// (`text`, `content`, `report`), so those are masked as prose while the rest of
// the envelope — event type, metrics, timings — passes through untouched.
function maskSseLine(line, streamMasker) {
  if (!line.startsWith('data: ')) return line;

  const body = line.slice(6);
  if (body === '[DONE]') return line;

  try {
    const parsed = JSON.parse(body);

    // `text`/`content` are token fragments of a narrative being streamed, so
    // they go through the boundary-aware masker — a name can be split across
    // two frames.
    const streamed = {};
    if (typeof parsed.text === 'string') {
      streamed.text = streamMasker.push(parsed.text);
      delete parsed.text;
    }
    if (typeof parsed.content === 'string') {
      streamed.content = streamMasker.push(parsed.content);
      delete parsed.content;
    }

    // Everything else in the envelope is a complete value, not a fragment:
    // alert rows ("Needs attention") arrive as one whole event carrying
    // `names`, `title` and `body`. Mask the event as an object so those fields
    // are covered — the earlier version only looked at text/content, which is
    // why alert rows kept their real names.
    learnIdentities(parsed);
    const maskedEvent = maskObject(parsed);

    return `data: ${JSON.stringify({ ...maskedEvent, ...streamed })}`;
  } catch (_e) {
    // Not JSON — mask the raw remainder as prose.
    return `data: ${streamMasker.push(body)}`;
  }
}

// Prose masking can only rewrite names it has been told about, and an AI
// narrative names people the payload itself never lists as fields. On a real
// dashboard the roster loads first and seeds them — but that is ordering luck,
// not a guarantee, and a cold-loaded insight would keep real names in its
// sentences. So before the first stream of a session, fetch the member list
// once and learn from it.
let rosterSeed = null;

// Kicked off once, never awaited by the request that triggers it. An earlier
// version awaited it inline, which put a 1000-user fetch in front of every
// insight and stream call and made the dashboard feel like it had hung.
function seedRoster(originalFetch) {
  if (rosterSeed) return rosterSeed;

  rosterSeed = (async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await originalFetch(`${SERVER_URL}/api/v1/users?limit=1000`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      learnIdentities(await res.json());
    } catch (_e) {
      // A demo still runs if this fails: field masking is unaffected, and the
      // masker keeps learning from every other payload that passes through.
    }
  })();

  return rosterSeed;
}

export function installDemoFetchPatch() {
  if (installed || typeof window === 'undefined' || !window.fetch) return;
  installed = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async function demoFetch(input, init) {
    const url0 = typeof input === 'string' ? input : (input && input.url) || '';
    // Start learning the roster the first time a narrative endpoint is hit, but
    // do not hold the request up for it — the stream masks whatever it knows by
    // the time each frame arrives, and identities also arrive from every other
    // payload. Blocking here is what made the dashboard hang.
    if (isDemoMode() && /stream|insight|brief|summary|intelligence|story|alert/i.test(url0)) {
      seedRoster(originalFetch);
    }

    const response = await originalFetch(input, init);

    if (!isDemoMode()) return response;

    const url = url0;
    if (isExcludedUrl(url)) return response;

    const contentType = response.headers.get('content-type') || '';
    if (!isMaskableContentType(contentType)) return response;

    /* ── streamed responses ── */
    if (contentType.includes('text/event-stream') && response.body) {
      const streamMasker = createStreamMasker();
      const decoder = new TextDecoder();
      const encoder = new TextEncoder();
      let lineBuffer = '';

      const masked = new ReadableStream({
        async start(controller) {
          const reader = response.body.getReader();
          try {
            for (;;) {
              const { done, value } = await reader.read();
              if (done) break;

              lineBuffer += decoder.decode(value, { stream: true });
              const lines = lineBuffer.split('\n');
              lineBuffer = lines.pop() || '';

              const out = lines.map((l) => maskSseLine(l, streamMasker)).join('\n');
              controller.enqueue(encoder.encode(out + '\n'));
            }

            // Release any partial line, then any token held back mid-name.
            let tail = lineBuffer ? maskSseLine(lineBuffer, streamMasker) : '';
            const heldTail = streamMasker.flush();
            if (heldTail) tail += heldTail;
            if (tail) controller.enqueue(encoder.encode(tail));
          } catch (err) {
            controller.error(err);
            return;
          }
          controller.close();
        },
      });

      return new Response(masked, {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
      });
    }

    /* ── plain JSON ── */
    try {
      const clone = response.clone();
      const data = await clone.json();

      learnIdentities(data);
      const maskedData = maskObject(data);

      return new Response(JSON.stringify(maskedData), {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
      });
    } catch (_e) {
      // Body was not valid JSON after all, or already consumed — hand back the
      // original rather than failing the request.
      return response;
    }
  };
}

export default installDemoFetchPatch;
